import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      // Pass through the verification error
      if (data.detail === 'email_not_verified') {
        throw new Error('email_not_verified');
      }
      throw new Error(data.detail || 'Login failed');
    }
    
    setUser(data);
    return data;
  };

  const register = async (email, password, name, language = 'de') => {
    const response = await fetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name, language }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }
    const userData = await response.json();
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = async () => {
    try {
      // Get Google OAuth URL from backend - include origin for dynamic redirect URI
      const response = await fetch(`${API}/auth/google/login`, {
        credentials: 'include',
        headers: {
          'Origin': window.location.origin
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Google OAuth
        window.location.href = data.auth_url;
      } else {
        throw new Error('Google OAuth not configured');
      }
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  const processGoogleCallback = async (token) => {
    // Process token from Google OAuth callback
    const response = await fetch(`${API}/auth/google/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token }),
    });
    if (!response.ok) {
      throw new Error('Google auth failed');
    }
    const userData = await response.json();
    setUser(userData);
    return userData;
  };

  const processGoogleSession = async (sessionId) => {
    // Legacy Emergent OAuth handling
    const response = await fetch(`${API}/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId }),
    });
    if (!response.ok) {
      throw new Error('Google auth failed');
    }
    const userData = await response.json();
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  const updateSettings = async (settings) => {
    const response = await fetch(`${API}/auth/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(settings),
    });
    if (response.ok) {
      const updatedUser = await response.json();
      setUser(updatedUser);
      return updatedUser;
    }
    throw new Error('Failed to update settings');
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      login,
      register,
      loginWithGoogle,
      processGoogleCallback,
      processGoogleSession,
      logout,
      updateSettings,
      checkAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
